import { useState, useEffect } from 'react';
import { Trash, Save, Pencil, Tv } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Database } from '@/lib/database.types';
import { showToast } from '@/utils/toast';
import { supabase } from '@/lib/supabase';

type ContentCoverage = Database['public']['Tables']['content_coverage']['Row'];

interface ContentCoverageManagementProps {
  providerId: number;
  versionId: number;
  regionId?: number;
}

interface CoverageForm {
  sports_coverage: 'extensive' | 'basic' | 'none';
  news_coverage: '24_7' | 'basic' | 'none';
  international_content: boolean;
  kids_family_content: boolean;
  premium_movies: boolean;
  local_channels: boolean;
  original_content: boolean;
}

export function ContentCoverageManagement({ providerId, versionId, regionId }: ContentCoverageManagementProps) {
  const [coverage, setCoverage] = useState<ContentCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<CoverageForm>({
    sports_coverage: 'none',
    news_coverage: 'none',
    international_content: false,
    kids_family_content: false,
    premium_movies: false,
    local_channels: false,
    original_content: false,
  });

  useEffect(() => {
    const fetchCoverage = async () => {
      try {
        setLoading(true);
        
        if (!regionId) {
          setCoverage(null);
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('content_coverage')
          .select('*')
          .eq('provider_id', providerId)
          .eq('version_id', versionId)
          .eq('region_id', regionId)
          .maybeSingle();
        
        if (error && error.code !== 'PGRST116') throw error;
        
        setCoverage(data);
        if (data) {
          setForm({
            sports_coverage: data.sports_coverage as 'extensive' | 'basic' | 'none' || 'none',
            news_coverage: data.news_coverage as '24_7' | 'basic' | 'none' || 'none',
            international_content: data.international_content || false,
            kids_family_content: data.kids_family_content || false,
            premium_movies: data.premium_movies || false,
            local_channels: data.local_channels || false,
            original_content: data.original_content || false,
          });
        }
      } catch (err) {
        showToast.error(err instanceof Error ? err.message : 'Failed to load content coverage');
      } finally {
        setLoading(false);
      }
    };

    if (providerId && versionId) {
      fetchCoverage();
    }
  }, [providerId, versionId, regionId]);

  const handleSave = async () => {
    if (!regionId) {
      showToast.required('Region');
      return;
    }

    try {
      setSaving(true);
      
      const coverageData = {
        provider_id: providerId,
        version_id: versionId,
        region_id: regionId,
        sports_coverage: form.sports_coverage,
        news_coverage: form.news_coverage,
        international_content: form.international_content,
        kids_family_content: form.kids_family_content,
        premium_movies: form.premium_movies,
        local_channels: form.local_channels,
        original_content: form.original_content,
      };

      console.log('Attempting to save coverage data:', coverageData);
      
      const { data, error } = await supabase
        .from('content_coverage')
        .upsert(coverageData, {
          onConflict: 'provider_id,version_id,region_id'
        })
        .select()
        .single();
      
      if (error) {
        console.error('Supabase upsert error:', error);
        console.error('Coverage data being saved:', coverageData);
        throw error;
      }
      
      setCoverage(data);
      setIsEditing(false);

      showToast.saved('Content coverage');
    } catch (err) {
      console.error('Error saving content coverage:', err);
      
      // Handle specific error cases
      if (err instanceof Error) {
        if (err.message.includes('duplicate key value violates unique constraint')) {
          showToast.constraintViolation('Content coverage');
        } else if (err.message.includes('23505')) {
          showToast.updated('Content coverage');
        } else {
          showToast.error(err.message);
        }
      } else {
        showToast.saveFailed('content coverage');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!coverage || !confirm('Are you sure you want to delete this content coverage?')) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('content_coverage')
        .delete()
        .eq('id', coverage.id);
      
      if (error) throw error;
      setCoverage(null);
      setForm({
        sports_coverage: 'none',
        news_coverage: 'none',
        international_content: false,
        kids_family_content: false,
        premium_movies: false,
        local_channels: false,
        original_content: false,
      });

      showToast.deleted('Content coverage');
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : 'Failed to delete content coverage');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (coverage) {
      setForm({
        sports_coverage: coverage.sports_coverage as 'extensive' | 'basic' | 'none' || 'none',
        news_coverage: coverage.news_coverage as '24_7' | 'basic' | 'none' || 'none',
        international_content: coverage.international_content || false,
        kids_family_content: coverage.kids_family_content || false,
        premium_movies: coverage.premium_movies || false,
        local_channels: coverage.local_channels || false,
        original_content: coverage.original_content || false,
      });
    }
  };

  const getSportsCoverageLabel = (coverage: string) => {
    const labels: Record<string, string> = {
      extensive: 'Extensive',
      basic: 'Basic',
      none: 'None'
    };
    return labels[coverage] || coverage;
  };

  const getNewsCoverageLabel = (coverage: string) => {
    const labels: Record<string, string> = {
      '24_7': '24/7',
      basic: 'Basic',
      none: 'None'
    };
    return labels[coverage] || coverage;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Content Coverage Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-8">
            <LoadingSpinner />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!regionId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Content Coverage Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Please select a region to manage content coverage. Content availability varies by region due to licensing agreements and local regulations.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Content Coverage Management</CardTitle>
          <div className="flex gap-2">
            {coverage && !isEditing && (
              <>
                <Button onClick={handleEdit} size="sm" variant="outline">
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={handleDelete} size="sm" variant="outline">
                  <Trash className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            {(!coverage || isEditing) && (
              <Button onClick={handleSave} size="sm" disabled={saving}>
                {saving ? <LoadingSpinner className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            )}
            {isEditing && (
              <Button onClick={handleCancel} size="sm" variant="outline">
                Cancel
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Configure content types and coverage levels available in this region.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {(!coverage || isEditing) ? (
            // Edit Form
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="sports-coverage">Sports Coverage</Label>
                <Select
                  value={form.sports_coverage}
                  onValueChange={(value: 'extensive' | 'basic' | 'none') => 
                    setForm(prev => ({ ...prev, sports_coverage: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="extensive">Extensive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="news-coverage">News Coverage</Label>
                <Select
                  value={form.news_coverage}
                  onValueChange={(value: '24_7' | 'basic' | 'none') => 
                    setForm(prev => ({ ...prev, news_coverage: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="24_7">24/7</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={form.international_content}
                  onCheckedChange={(checked) => 
                    setForm(prev => ({ ...prev, international_content: checked }))
                  }
                />
                <Label>International Content</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={form.kids_family_content}
                  onCheckedChange={(checked) => 
                    setForm(prev => ({ ...prev, kids_family_content: checked }))
                  }
                />
                <Label>Kids & Family Content</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={form.premium_movies}
                  onCheckedChange={(checked) => 
                    setForm(prev => ({ ...prev, premium_movies: checked }))
                  }
                />
                <Label>Premium Movies</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={form.local_channels}
                  onCheckedChange={(checked) => 
                    setForm(prev => ({ ...prev, local_channels: checked }))
                  }
                />
                <Label>Local Channels</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={form.original_content}
                  onCheckedChange={(checked) => 
                    setForm(prev => ({ ...prev, original_content: checked }))
                  }
                />
                <Label>Original Content</Label>
              </div>
            </div>
          ) : (
            // Display View
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm text-muted-foreground">Sports Coverage</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Tv className="h-4 w-4" />
                    <Badge variant={coverage.sports_coverage === 'extensive' ? 'default' : coverage.sports_coverage === 'basic' ? 'secondary' : 'outline'}>
                      {getSportsCoverageLabel(coverage.sports_coverage || 'none')}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground">News Coverage</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Tv className="h-4 w-4" />
                    <Badge variant={coverage.news_coverage === '24_7' ? 'default' : coverage.news_coverage === 'basic' ? 'secondary' : 'outline'}>
                      {getNewsCoverageLabel(coverage.news_coverage || 'none')}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Badge variant={coverage.international_content ? 'default' : 'outline'}>
                    {coverage.international_content ? '✓' : '✗'}
                  </Badge>
                  <Label className="text-sm">International Content</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant={coverage.kids_family_content ? 'default' : 'outline'}>
                    {coverage.kids_family_content ? '✓' : '✗'}
                  </Badge>
                  <Label className="text-sm">Kids & Family</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant={coverage.premium_movies ? 'default' : 'outline'}>
                    {coverage.premium_movies ? '✓' : '✗'}
                  </Badge>
                  <Label className="text-sm">Premium Movies</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant={coverage.local_channels ? 'default' : 'outline'}>
                    {coverage.local_channels ? '✓' : '✗'}
                  </Badge>
                  <Label className="text-sm">Local Channels</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant={coverage.original_content ? 'default' : 'outline'}>
                    {coverage.original_content ? '✓' : '✗'}
                  </Badge>
                  <Label className="text-sm">Original Content</Label>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}